import { alpha, Box, Stack, Typography } from "@mui/material";
import {
  INSIGHT_PANEL_COMPACT,
  INSIGHT_PANEL_NARROW,
  READABLE_BODY_SX,
  READABLE_SECONDARY_SX,
} from "src-next/modules/clinical-insights/constants/clinicalInsightsTheme";
import { latestItemDate } from "./insightPatientsUtils";
import type { InsightPatientItem } from "src-next/modules/clinical-insights/types/insightWorkspaceTypes";

interface InsightPatientExpandedPanelProps {
  items: InsightPatientItem[];
  accentColor: string;
  itemColumnLabel: string;
  emptyRowText: string;
}

const GRID_COLUMNS = "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1.2fr) 88px";

const headerLabelSx = {
  ...READABLE_SECONDARY_SX,
  fontWeight: 600,
  letterSpacing: "0.02em",
  color: "text.secondary",
} as const;

export function InsightPatientExpandedPanel({
  items,
  accentColor,
  itemColumnLabel,
  emptyRowText,
}: InsightPatientExpandedPanelProps) {
  if (items.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ ...READABLE_SECONDARY_SX, fontStyle: "italic" }}>
        {emptyRowText}
      </Typography>
    );
  }

  const lastDate = latestItemDate(items);

  return (
    <Box
      sx={{
        position: "relative",
        my: 1,
        ml: 0.5,
        py: 1.75,
        pl: 2.25,
        pr: 1.5,
        borderRadius: 2.5,
        bgcolor: (theme) => alpha(theme.palette.text.primary, 0.02),
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 14,
          bottom: 14,
          width: 3,
          borderRadius: 4,
          bgcolor: alpha(accentColor, 0.42),
        },
        [INSIGHT_PANEL_COMPACT]: { pl: 2, pr: 1.25, py: 1.5 },
      }}
    >
      <Typography
        sx={{
          ...READABLE_SECONDARY_SX,
          fontWeight: 600,
          color: "text.secondary",
          mb: 1.25,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        Última data:{" "}
        <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
          {lastDate}
        </Box>
      </Typography>

      <Box
        className="expanded-header"
        sx={{
          display: "grid",
          gridTemplateColumns: GRID_COLUMNS,
          gap: 1.25,
          pb: 1.25,
          mb: 0.5,
          [INSIGHT_PANEL_NARROW]: { display: "none" },
        }}
      >
        <Typography sx={headerLabelSx}>{itemColumnLabel}</Typography>
        <Typography sx={headerLabelSx}>Situação</Typography>
        <Typography sx={headerLabelSx}>Profissional</Typography>
        <Typography sx={{ ...headerLabelSx, textAlign: "right" }}>Data</Typography>
      </Box>

      <Stack spacing={0.75}>
        {items.map((item, idx) => {
          const activate = item.onActivate;
          return (
          <Box
            key={`${item.itemKey}-${item.statusKey}-${idx}`}
            className="expanded-row"
            role={activate ? "button" : undefined}
            tabIndex={activate ? 0 : undefined}
            title={activate ? "Ver respostas" : undefined}
            onClick={
              activate
                ? (event) => {
                    event.stopPropagation();
                    activate();
                  }
                : undefined
            }
            onKeyDown={
              activate
                ? (event) => {
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    event.stopPropagation();
                    activate();
                  }
                : undefined
            }
            sx={{
              display: "grid",
              gridTemplateColumns: GRID_COLUMNS,
              gap: 1.25,
              alignItems: "center",
              py: 1,
              px: 1,
              mx: -1,
              borderRadius: 1.5,
              cursor: activate ? "pointer" : undefined,
              transition: "background-color 0.15s ease",
              "&:hover": {
                bgcolor: (theme) =>
                  activate
                    ? alpha(accentColor, 0.08)
                    : alpha(theme.palette.text.primary, 0.03),
              },
              [INSIGHT_PANEL_NARROW]: {
                gridTemplateColumns: "1fr",
                gap: 0.75,
                alignItems: "start",
                py: 1.25,
                borderBottom: "1px solid",
                borderColor: "divider",
                "&:last-child": { borderBottom: "none" },
              },
            }}
          >
            <Typography
              sx={{
                ...READABLE_BODY_SX,
                fontWeight: 600,
                fontSize: "1.125rem",
                lineHeight: 1.4,
                wordBreak: "break-word",
                color: activate ? accentColor : "text.primary",
                textDecoration: activate ? "underline" : undefined,
                textUnderlineOffset: activate ? "3px" : undefined,
              }}
            >
              {item.label}
            </Typography>
            <Typography
              component="span"
              title={item.statusLabel}
              sx={{
                ...READABLE_BODY_SX,
                fontWeight: 600,
                px: 1.35,
                py: 0.45,
                borderRadius: 10,
                width: "max-content",
                maxWidth: "100%",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                border: "1px solid",
                borderColor: alpha(accentColor, 0.22),
                bgcolor: alpha(accentColor, 0.05),
                color: accentColor,
              }}
            >
              {item.statusLabel}
            </Typography>
            <Typography sx={{ ...READABLE_BODY_SX, color: "text.secondary", fontWeight: 400 }}>
              {item.practitioner}
            </Typography>
            <Typography
              sx={{
                ...READABLE_BODY_SX,
                textAlign: "right",
                whiteSpace: "nowrap",
                color: "text.secondary",
                fontWeight: 400,
                fontVariantNumeric: "tabular-nums",
                [INSIGHT_PANEL_NARROW]: { textAlign: "left" },
              }}
            >
              {item.date}
            </Typography>
          </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
